<template>
  <v-container class="px-0">
		<ToolbarDaBusca
			:filtro="filtro"
			@buscar="buscar"
		/>

		<div v-if="Object.keys(filtro).length">
			<!-- <v-chip
				v-if="filtro.minhaSomar"
				class="mx-1 mb-1"
				close
				@click:close="() => { filtro.minhaSomar = false }"
			> Somar Itapocu </v-chip> -->
			<v-chip
				v-if="filtro.loja"
				class="mx-1 mb-1"
				close
				@click:close="() => {
					filtro = {
						...filtro,
						loja: null,
					}
				}"
			> {{ filtro.loja.text }} </v-chip>
			<template v-for="situacao in filtro.situacoes">
				<v-chip
					:key="situacao"
					class="mx-1 mb-1"
					close
					@click:close="() => {
						filtro = {
							...filtro,
							situacoes: filtro.situacoes.filter(situacaoFiltro => situacaoFiltro !== situacao),
						}
					}"
				> {{ situacao }} </v-chip>
			</template>
			<template v-if="filtro.buscarEm && !authModule.éVisitante">
				<v-chip
					v-for="buscarEm in filtro.buscarEm"
					:key="buscarEm"
					class="mx-1 mb-1"
					close
					@click:close="() => {
						if (!filtro.buscarEm) return
						filtro = {
							...filtro,
							buscarEm: filtro.buscarEm.filter(buscarEmFiltro => buscarEmFiltro !== buscarEm),
						}
					}"
				> {{ OPCOES_DE_BUSCAR_EM.find(opcao => opcao.valor === buscarEm)?.descricao }} </v-chip>
			</template>
			<v-chip
				v-if="filtro.recentes"
				class="mx-1 mb-1"
				close
				@click:close="() => {
					filtro = {
						...filtro,
						recentes: false,
					}
				}"
			> Novos </v-chip>
		</div>

		<v-card flat>
			<v-list subheader>
				<template>
					<template v-if="buscando">
						<AppLoading/>
					</template>
					<template v-else-if="!temBusca">
						<NoRecentSearchs/>
					</template>
					<template v-else-if="resultados">
						<NoResultsFound v-if="!temDados" class="mt-4" />
						<template v-else>
							<v-subheader>
								<span> Resultados... </span>
								<v-spacer/>
								<v-icon style="cursor:pointer;"
									@click="onClickLimparBusca"
								>mdi-close</v-icon>
							</v-subheader>
							<template v-for="pagina in resultados.pages">
								<ItemDaBusca
									v-for="item in pagina.items"
									:key="`${item.resultType}-${item._id}`"
									:item="item"
								/>
							</template>

							<div
								ref="infiniteScrollTrigger"
								class="text-center py-4"
							>
								<v-progress-circular v-if="isFetchingNextPage" indeterminate color="primary" />
							</div>
						</template>
					</template>
				</template>
			</v-list>
		</v-card>
  </v-container>
</template>

<script setup lang="ts">
import NoRecentSearchs from '@/components/ui/NoRecentSearchs.vue'
import NoResultsFound from '@/components/ui/NoResultsFound.vue'
import { ParametrosDoFind, ResultadoBusca } from '../../../common/types/Busca'
import SearchService from '@/services/SearchService'
import ItemDaBusca from './components/ItemDaBusca.vue'
import ToolbarDaBusca from './components/ToolbarDaBusca.vue'
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue'
import { useInfiniteQuery, useQueryClient } from '@tanstack/vue-query'
import { CamposDoFiltroDeBusca, obterFiltroDefault, OPCOES_DE_BUSCAR_EM } from '@/shareds/sharedBusca'
import { application, authModule, userModule } from '@/store'
import { useRoute, useRouter } from 'vue2-helpers/vue-router'
import { SituacaoDoMacon } from 'common/types/Irmao'
import { arraysAreEqual } from '../../../common/shareds/utils'
import { Dictionary } from 'vue-router/types/router'
import dayjs from 'dayjs'

const temBusca = ref(true)

const route = useRoute()
const router = useRouter()

function converterParaParametrosDoFind(params: CamposDoFiltroDeBusca): ParametrosDoFind {
	return {
		search: params.busca,
		loja: params.loja ? params.loja.value : undefined,
		somar: params.minhaSomar && userModule.currentUser?.role !== 'Visitante' ? userModule.currentUser?.somar?._id : undefined,
		recentes: params.recentes,
		situacoes: Array.isArray(params.situacoes) ? params.situacoes : (params.situacoes ? [params.situacoes] : []),
		buscarEm: authModule.éVisitante
			? ['servico']
			: Array.isArray(params.buscarEm) ? params.buscarEm : (params.buscarEm ? [params.buscarEm] : undefined),
	}
}

const filtro = computed<CamposDoFiltroDeBusca>({
	get() {
		const filtroDefault = obterFiltroDefault()
		const { query } = route

		const situacoes = typeof query.situacoes === 'string' ? [query.situacoes as SituacaoDoMacon] : ((query.situacoes as (SituacaoDoMacon[] | undefined)) || filtroDefault.situacoes)
		const buscarEm = typeof query.buscarEm === 'string' ? [query.buscarEm as ResultadoBusca['resultType']] : ((query.buscarEm as (ResultadoBusca['resultType'][] | undefined)) || filtroDefault.buscarEm)

		return {
			...filtroDefault,
			...query,
			recentes: query.recentes === 'true',
			minhaSomar: query.minhaSomar !== 'false',
			loja: typeof query.loja === 'string' ? JSON.parse(query.loja) : null,
			situacoes,
			buscarEm,
		} satisfies CamposDoFiltroDeBusca
	},
	set(params: CamposDoFiltroDeBusca) {
		const filtroDefault = obterFiltroDefault()

		const query: Dictionary<string | (string | null)[] | null | undefined> = {}
		if (params.busca) {
			query.busca = params.busca
		}
		if (params.loja) {
			query.loja = JSON.stringify({ text: params.loja.text, value: params.loja.value })
		}
		if (params.buscarEm && params.buscarEm.length !== OPCOES_DE_BUSCAR_EM.length) {
			query.buscarEm = params.buscarEm
		}
		if (!arraysAreEqual(params.situacoes, filtroDefault.situacoes)) {
			query.situacoes = params.situacoes
		}
		if (!params.minhaSomar) {
			query.minhaSomar = 'false'
		}
		if (params.recentes) {
			query.recentes = 'true'
		}
		router.replace({
			query,
		}).catch((err) => {
			if (
				err.name !== 'NavigationDuplicated' &&
				!err.message.includes('Avoided redundant navigation to current location')
			) {
				console.error(err);
			}
		})
	}
})

const findParams = computed<ParametrosDoFind>(() => converterParaParametrosDoFind(filtro.value))

const queryClient = useQueryClient()

const { data: resultados, isLoading: buscando, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery({
	queryKey: ['busca', findParams],
	queryFn: ({ pageParam }) => {
		const params = findParams.value
		return SearchService.find({ pagina: pageParam, ...findParams.value })
			.then((resultados) => {
				if (params.recentes && params?.buscarEm?.includes('servico')) {
					application.visualizar({ ultVisualizacaoDeServicos: dayjs().toISOString() })
				}
				return resultados
			})
	},
	initialPageParam: 1,
	getNextPageParam: (lastPage) => lastPage.paginacao.pagina < lastPage.totalDePaginas
		? lastPage.paginacao.pagina + 1
		: undefined,
})

const temDados = computed(() => !!resultados.value?.pages.flatMap(pagina => pagina.items).length)

async function buscar(novoFiltro: CamposDoFiltroDeBusca) {
	filtro.value = novoFiltro
	nextTick(() => {
		queryClient.invalidateQueries({
			queryKey: ['busca', findParams],
		})
	})
}

function onClickLimparBusca() {
	temBusca.value = false
}

const infiniteScrollTrigger = ref(null);
let observer!: IntersectionObserver;

watch(() => infiniteScrollTrigger.value, (current, old) => {
  if (!!current && current !== old) {
    observer.observe(current);
  }
})

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && hasNextPage.value) {
      fetchNextPage();
    }
  });

  if (infiniteScrollTrigger.value) {
    observer.observe(infiniteScrollTrigger.value);
  }
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});
</script>

<style>
#styled-input {
  font-size: 10pt;
}
.styled-input label[for] {
  font-size: 10pt;
}
</style>